import { useEffect, useMemo, useState } from 'preact/hooks';
import './text_input.scss';
import { ComponentChild } from 'preact';
import { useEventCallback } from '@brainfocus/web/utils/react/use_event_callback';

export function TextInput<TOutput extends OutputType>({
    value,
    required,
    expression,
    label,
    name,
    output,
    disabled,
    onChange,
    placeholder,
    type,
    children,
    autocomplete,
    mask,
}: TextInputProps<TOutput>) {
    const [focused, setFocused] = useState(false);
    const [touched, setTouched] = useState(false);
    const [currentValue, setCurrentValue] = useState(value);
    const [valueStr, setValueStr] = useState(readValueProp(value));

    const error = useMemo<boolean>(
        () => {
            return touched && !validate(currentValue, !!required, expression);
        },
        [currentValue, touched, required, expression],
    );

    // Runs when the value prop changes; compares against the current value without watching it.
    const syncFromProp = useEventCallback((propValue: typeof value) => {
        const bothNans = typeof propValue === 'number' && isNaN(propValue)
            && typeof currentValue === 'number' && isNaN(currentValue);

        if (propValue !== currentValue && !bothNans) {
            setValueStr(readValueProp(propValue));
            setCurrentValue(propValue);
        }
    });

    useEffect(() => {
        syncFromProp(value);
    }, [value, syncFromProp]);

    // Prefills from a posted value when the field name changes, if the field is still empty.
    const prefillFromPost = useEventCallback((fieldName: typeof name) => {
        const meta = document.querySelector(`meta[name="post:${fieldName}"]`) as HTMLMetaElement;
        const postVal = meta?.content ?? '';
        if (postVal && !value && !currentValue) {
            adaptValue(postVal);
        }
    });

    useEffect(() => {
        prefillFromPost(name);
    }, [name, prefillFromPost]);

    const classes = ['bf-text-input-field', 'bf-input'];

    if (disabled) {
        classes.push('bf-input-disabled');
    } else if (focused) {
        classes.push('bf-input-focused');
    } else if (error) {
        classes.push('bf-input-error');
    }

    return (
        <label class={classes.join(' ')}>
            <div class='bf-input-name'>{label}</div>
            <input
                type={type ?? 'text'}
                name={name}
                autocomplete={autocomplete}
                required={required ? true : false}
                placeholder={placeholder || ''}
                onFocus={() => (setTouched(true), setFocused(true))}
                onBlur={() => setFocused(false)}
                value={valueStr !== undefined ? valueStr.toString() : ''}
                onChange={e => adaptValue((e.target as HTMLInputElement).value)}
                onInput={mask ? e => applyMask(e.target as HTMLInputElement) : undefined}
                disabled={!!disabled}
            />
            {children}
        </label>
    );

    function applyMask(el: HTMLInputElement): void {
        const masked = mask!(el.value);
        // Preact skips the DOM write when the rendered value is unchanged, so
        // rejected characters must be cleared on the element directly.
        if (el.value !== masked) {
            el.value = masked;
        }
        adaptValue(masked);
    }

    function adaptValue(stringVal: string): void {
        if (mask) {
            stringVal = mask(stringVal);
        }
        let outVal: TOutput | undefined;

        if (output === 'number') {
            if (stringVal === '') {
                outVal = undefined;
            } else {
                outVal = parseFloat(stringVal) as TOutput;
            }
        } else {
            outVal = stringVal as TOutput;
        }

        if (!validate(outVal, !!required, expression)) {
            outVal = undefined;
        }

        setValueStr(stringVal);
        setCurrentValue(outVal);
        onChange?.(outVal);
    }

    function readValueProp(value: string | number | undefined): string {
        return value === undefined
            ? ''
            : typeof value === 'number' && isNaN(value)
                ? ''
                : value.toString();
    }
}

function validate(value: string | number | undefined, required: boolean, expression?: RegExp): boolean {
    const requiredError = required
        && (value === '' || value === undefined);
    const nanError = typeof value === 'number' && isNaN(value);
    const expError = expression && typeof value === 'string'
        && !expression.test(value);
    return !requiredError && !nanError && !expError;
}

interface TextInputProps<TOutput extends OutputType> {
    /**
     * Name used on the html input element
     */
    name: string;
    /**
     * Label displayed as part of the component
     */
    label: string;
    output: TOutput extends string ? 'string' : 'number';
    required?: boolean;
    /**
     * Type set on the input element
     * @default 'text'
     */
    type?: string;
    value?: TOutput | undefined;
    disabled?: boolean;
    autocomplete?: string;
    /**
     * Expression the value needs to match
     */
    expression?: RegExp;
    /**
     * Masks the raw text into its canonical form on every keystroke (e.g. a
     * fixed time pattern). The masked value is written back to the element and
     * emitted through `onChange`.
     */
    mask?: (raw: string) => string;
    placeholder?: string;
    onChange?(val: TOutput | undefined): void;
    children?: ComponentChild;
}

type OutputType = string | number;
